import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CopilotActions } from './copilot.actions';
import { catchError, filter, map, mergeMap, of, switchMap, take } from 'rxjs';
import { CopilotApiService } from '../services/copilot.service';
import { Message } from '../models/copilot.models';
import { tap } from 'rxjs/operators';
import {
  selectedClientId,
  selectMessagesByConversation,
} from './copilot.selectors';
import { Store } from '@ngrx/store';

@Injectable({
  providedIn: 'root'
})
export class CopilotEffects {
  private actions$: Actions = inject(Actions);
  private api: CopilotApiService = inject(CopilotApiService);
  private _store = inject(Store);

  loadConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopilotActions.loadConversations),
      mergeMap(({ clientId }) =>
        this.api.fetchConversations(clientId).pipe(
          map((conversations) =>
            CopilotActions.loadConversationsSuccess({ clientId, conversations })
          ),
          catchError((error) =>
            of(CopilotActions.loadConversationsFailure({ clientId, error }))
          )
        )
      )
    )
  );

  loadMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopilotActions.loadMessages),
      // Combine with the latest messages for that conversation from store
      switchMap(({ clientId, conversationId, forceRefresh }) =>
        this._store
          .select(selectMessagesByConversation(clientId, conversationId))
          .pipe(
            take(1),
            switchMap((cachedMessages) => {
              // ✅ If we already have messages and we're not forcing refresh,
              // skip API call and return success immediately.
              if (Array.isArray(cachedMessages) && cachedMessages.length && !forceRefresh) {
                return of(
                  CopilotActions.loadMessagesSuccess({
                    clientId,
                    conversationId,
                    messages: cachedMessages,
                    fromCache: true,
                  })
                );
              }

              // Otherwise, proceed to API call
              return this.api.fetchMessages(clientId, conversationId).pipe(
                map((messages) =>
                  CopilotActions.loadMessagesSuccess({
                    clientId,
                    conversationId,
                    messages,
                  })
                ),
                catchError((error) => {
                  const message =
                    typeof error === 'string'
                      ? error
                      : error?.message || 'Something went wrong loading messages.';
                  this.api.reportLoadMessagesError(message);
                  return of(
                    CopilotActions.loadMessagesFailure({
                      clientId,
                      conversationId,
                      error,
                    })
                  );
                })
              );
            })
          )
      )
    )
  );


  sendMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopilotActions.sendMessage),
      switchMap(({ clientId, conversationId, content, fileName }) =>
        this.api.sendMessage(clientId, conversationId, content, fileName).pipe(
          map((message: Message) => {
            message.content = message.content?.replace(/\\n/g, '\n') || '';
            return CopilotActions.messageReceived({
              clientId,
              conversationId,
              message,
            });
          }),
          catchError(() =>
            of(CopilotActions.stopStreaming({ clientId, conversationId }))
          )
        )
      )
    )
  );

  messageReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopilotActions.messageReceived),
      filter(({ message }) => message.role === 'assistant'),
      switchMap(({ clientId, conversationId }) =>
        this._store.select(selectedClientId).pipe(
          take(1),
          filter((selectedId) => selectedId !== clientId),
          map(() => CopilotActions.triggerConversationHighlight({ clientId, conversationId }))
        )
      )
    )
  );

}
